import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import RequestFeePanel from '../../components/admin2/RequestFeePanel';
import { fetchFeeQueue, adminFeeStatusLabel } from '../../lib/adminRequestFees';

// Request fees (299 SAR) — the refund queue first. The daily cron moves a fee
// to 'refund_due' when no verified factory reached the trader within 10 Saudi
// working days; it never refunds. This page is where a person does: refund in
// Telr, then record the reference. Holidays are the admin's call, case by case
// (owner, 2026-09-29) — "Flag refund due" on a paid fee covers that.
const TABS = ['refund_due', 'paid', 'awaiting_payment', 'all'];

export default function ConsoleRequestFees({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('refund_due');
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 1900); };

  const load = useCallback(async () => {
    try { setRows(await fetchFeeQueue()); } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => (rows || []).reduce((acc, f) => { acc[f.status] = (acc[f.status] || 0) + 1; return acc; }, {}), [rows]);
  const list = useMemo(() => {
    const r = rows || [];
    const f = tab === 'all' ? r : r.filter((x) => x.status === tab);
    // Paid: nearest deadline first — that is the order they become refund-due.
    return tab === 'paid'
      ? [...f].sort((a, b) => String(a.refund_deadline_at || '').localeCompare(String(b.refund_deadline_at || '')))
      : f;
  }, [rows, tab]);

  const title = (req) => (isAr ? (req?.title_ar || req?.title_en) : (req?.title_en || req?.title_ar)) || (isAr ? 'طلب' : 'Request');
  const trader = (req) => req?.requester?.company_name || req?.requester?.full_name || (isAr ? 'تاجر' : 'Trader');
  const openRequest = (req) => nav(req?.request_kind === 'direct' ? `/admin2/requests/${req.id}` : `/admin2/managed/${req.id}`);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="request-fees">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'رسوم تأكيد الطلب' : 'Request fees'}</h1>
            <p className="ac-sub">
              {isAr ? '٢٩٩ ر.س على الطلب المُدار وعلى «فريق مَعبر يبحث لك». لا استرداد تلقائي.'
                : 'SAR 299 on managed requests and on "Maabar team finds a factory". No automatic refunds.'}
            </p>
          </div>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 16 }}>
          {TABS.map((k) => (
            <button key={k} className={`ac-chip${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
              {k === 'all' ? (isAr ? 'الكل' : 'All') : adminFeeStatusLabel(k, isAr)}
              {k !== 'all' && counts[k] ? ` · ${counts[k]}` : ''}
            </button>
          ))}
        </div>

        {rows == null ? (
          <div className="ac-skel" style={{ height: 90 }} />
        ) : list.length === 0 ? (
          <div className="ac-placeholder">{isAr ? 'لا شيء هنا.' : 'Nothing here.'}</div>
        ) : list.map((f) => (
          <div key={f.id} style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, marginBottom: 6 }}>
              <button className="ac-btn ac-btn-sm" onClick={() => openRequest(f.request)}>{f.request?.request_ref || (isAr ? 'فتح' : 'Open')}</button>
              <span style={{ fontWeight: 700, color: 'var(--ac-ink)' }}>{title(f.request)}</span>
              <span style={{ color: 'var(--ac-muted)' }}>· {trader(f.request)}</span>
            </div>
            <RequestFeePanel fee={f} requestId={f.request_id} isAr={isAr} onChanged={load} flash={flash} />
          </div>
        ))}
      </div>
      {toast && <div className="ac-toast" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{toast}</div>}
    </ConsoleShell>
  );
}
